/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Reports.tsx
 * PURPOSE: Reports page - Vietnamese F&B reporting and analytics hub
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * DATE: 2026-01-15
 * COMPLIANCE: SDLC 6.3.1 + NQH-BRAND-DESIGN-SYSTEM.md
 *
 * @business-context
 * - Domain: Vietnamese F&B Platform, Reporting, Analytics
 * - Impact: Business Intelligence, Manager Decision Making
 * - Stakeholders: Managers, Admins, Chairman
 *
 * @technical-architecture
 * - Layer: Frontend Page Layer
 * - Pattern: Feature Page, Navigation Hub Pattern
 * - Dependencies: React, react-router-dom, Design System Components
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Navigation: frontend/src/utils/navigation.ts (reports item)
 * - API: docs/03-integrate/01-API-Specifications/ (Sprint 10 reports)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon, DocumentArrowDownIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBgColor: string;
  iconColor: string;
  route: string;
  available: boolean;
}

const REPORT_CARDS: ReportCard[] = [
  {
    id: 'labor-cost',
    title: 'Chi phí nhân công',
    description: 'Phân tích chi phí lao động theo chi nhánh, ca làm',
    icon: ChartBarIcon,
    iconBgColor: 'bg-primary-100',
    iconColor: 'text-primary-600',
    route: '/cfo/labor-cost',
    available: true,
  },
  {
    id: 'attendance',
    title: 'Chấm công',
    description: 'Báo cáo giờ làm việc, OT, nghỉ phép',
    icon: CalendarIcon,
    iconBgColor: 'bg-green-100',
    iconColor: 'text-green-600',
    route: '/attendance',
    available: true,
  },
  {
    id: 'overtime',
    title: 'Tăng ca (OT)',
    description: 'Thống kê giờ tăng ca theo Vietnamese Labor Law',
    icon: ClockIcon,
    iconBgColor: 'bg-orange-100',
    iconColor: 'text-orange-600',
    route: '/attendance',
    available: true,
  },
  {
    id: 'export',
    title: 'Xuất báo cáo',
    description: 'Tải xuống Excel/PDF cho BHXH, kế toán',
    icon: DocumentArrowDownIcon,
    iconBgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
    route: '/cfo/labor-cost',
    available: true,
  },
];

/**
 * Reports Page Component
 *
 * Navigation hub for Vietnamese F&B reporting and analytics.
 * Per navigation.ts: 'reports' feature for Manager+ roles
 *
 * @returns Reports page with navigation to available report modules
 */
export const Reports: React.FC = () => {
  const navigate = useNavigate();

  const handleCardClick = (card: ReportCard) => {
    if (card.available) {
      navigate(card.route);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Báo cáo</h1>
        <p className="mt-1 text-sm text-gray-500">
          Tạo và xuất báo cáo hoạt động kinh doanh
        </p>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {REPORT_CARDS.map((card) => {
          const IconComponent = card.icon;
          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`bg-white rounded-lg shadow p-6 border border-gray-200 transition-all ${
                card.available
                  ? 'hover:border-primary-500 hover:shadow-md cursor-pointer'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 ${card.iconBgColor} rounded-lg flex-shrink-0`}>
                  <IconComponent className={`h-6 w-6 ${card.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{card.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{card.description}</p>
                  {!card.available && (
                    <span className="inline-block mt-2 text-xs text-gray-400">Sprint 10</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Thông tin nhanh</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Báo cáo khả dụng</p>
            <p className="text-2xl font-bold text-gray-900">
              {REPORT_CARDS.filter(c => c.available).length}/{REPORT_CARDS.length}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Trang chi tiết</p>
            <p className="text-2xl font-bold text-primary-600">CFO Dashboard</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Hỗ trợ xuất</p>
            <p className="text-2xl font-bold text-green-600">Excel, PDF</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;